<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    transition="fade-transition"
    persistent
    ref="dialog"
    v-model="isOpen"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>

    <v-card style="width: 560px" v-if="isOpen">
      <v-card-text height="800px" class="px-8 pt-8">
        <div class="mb-8 d-flex align-start">
          <div class="d-flex flex-column">
            <span class="font-weight-light p-1 base--text">
              {{ $t("heading.onboarding.zipDeploy.title") }}
            </span>
            <h4 class>{{ $t("heading.onboarding.zipDeploy.subtitle") }}</h4>
          </div>

          <v-btn
            x-small
            icon
            :disabled="loading"
            @click="$emit('remove-modal')"
            class="ml-auto"
          >
            <v-icon size="24" color="gray">$close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-5"/>

        <div class="form-box">
          <span class="gray--text text--darken-1 mb-1 d-block">
            {{ $t("heading.onboarding.zipDeploy.file.label") }}
          </span>

          <div
            class="drop-zone mt-2"
            :class="{
              'drop-zone--active': isDragOver,
              'drop-zone--disabled': isDropZoneDisabled,
              'drop-zone--reading': isReadingDrop,
            }"
            @click="onDropZoneClick"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <v-progress-circular
              v-if="isReadingDrop"
              indeterminate
              size="32"
              width="3"
              color="primary"
              class="mb-2"
            />
            <v-icon
              v-else
              size="32"
              :color="isDragOver ? 'primary' : 'gray'"
              class="mb-2"
            >
              $filetext
            </v-icon>
            <p class="drop-zone__title p-2 font-weight-medium mb-1">
              {{ $t("heading.onboarding.zipDeploy.dropZone.title") }}
            </p>
            <p class="caption gray--text text--darken-1 mb-0 text-center">
              {{ $t("heading.onboarding.zipDeploy.dropZone.hint") }}
            </p>
          </div>

          <button
            type="button"
            class="select-folder-link caption primary--text mt-2"
            :disabled="isDropZoneDisabled"
            @click.stop="triggerFolderInput"
          >
            {{ $t("heading.onboarding.zipDeploy.file.selectFolder") }}
          </button>

          <input
            ref="fileInput"
            type="file"
            class="d-none"
            multiple
            accept=".zip,.tar.gz,.sql,.sql.gz,.sqlite,.db,application/zip,application/gzip,application/x-gzip,application/sql,text/plain,application/x-sqlite3"
            @change="onFilesSelected"
          />
          <input
            ref="folderInput"
            type="file"
            class="d-none"
            webkitdirectory
            @change="onFolderSelected"
          />

          <div v-if="selections.length" class="selection-list mt-3">
            <div
              v-for="selection in selections"
              :key="selection.id"
              class="selection-list__item d-flex align-center"
            >
              <v-icon size="18" color="gray" class="mr-2">$filetext</v-icon>
              <span class="p-2 flex-grow-1 text-truncate">{{ selection.label }}</span>
              <v-btn
                x-small
                icon
                :disabled="loading"
                @click="removeSelection(selection.id)"
              >
                <v-icon size="18" color="gray">$close</v-icon>
              </v-btn>
            </div>
          </div>

          <div class="supported-formats mt-4">
            <span class="caption font-weight-medium gray--text text--darken-1 d-block mb-1">
              {{ $t("heading.onboarding.zipDeploy.supportedFormats.title") }}
            </span>
            <ul class="supported-formats__list caption gray--text text--darken-1 mb-0 pl-4">
              <li
                v-for="formatKey in supportedFormatKeys"
                :key="formatKey"
              >
                {{ $t(`heading.onboarding.zipDeploy.supportedFormats.${formatKey}`) }}
              </li>
            </ul>
            <p class="caption gray--text text--darken-1 mb-0 mt-2">
              {{ $t("heading.onboarding.zipDeploy.file.maxSelections") }}
            </p>
          </div>
        </div>

        <v-scale-transition>
          <div class="error-box mt-5" v-if="errorMessage">
            <v-icon color="error" size="20" class="mr-2">$alertcircle</v-icon>
            <span class="error--text">
              <b class="error--text">
                <i18next :translation="$t('message.alert.failed')">
                  <template #message>
                    <span class="font-weight-medium">{{ errorMessage }}</span>
                  </template>
                </i18next>
              </b>
            </span>
          </div>
        </v-scale-transition>
      </v-card-text>

      <v-card-actions class="pt-8 d-flex align-center justify-space-between">
        <div>
          <StepIndicator :currentStep="2" :maxSteps="4"/>
        </div>
        <div class="d-flex align-center">
          <v-btn
            large
            :disabled="loading"
            @click="$emit('remove-modal')"
            text
            elevation="0"
            class="mr-3"
          >
            <span class="font-weight-bold gray--text text--darken-1">
              {{ $t("button.cancel") }}
            </span>
          </v-btn>
          <v-btn
            large
            @click="submit"
            :disabled="isContinueDisabled"
            color="primary"
            elevation="0"
            class="primary"
            :loading="loading"
          >
            <span class="font-weight-bold">
              {{ $t("button.continue") }}
              <v-icon size="20" class="mr-0 ml-3">$arrowright</v-icon>
            </span>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StepIndicator from "../StepIndicator.vue";

const allowedStandaloneExtensions = [
  "zip",
  "tar.gz",
  "sql",
  "sql.gz",
  "sqlite",
  "db",
];

export const deployFromZipUploadMethods = {
  getFileExtension(name) {
    const lower = (name || "").toLowerCase();
    if (lower.endsWith(".sql.gz")) {
      return "sql.gz";
    }
    if (lower.endsWith(".tar.gz")) {
      return "tar.gz";
    }
    if (lower.endsWith(".zip")) {
      return "zip";
    }
    if (lower.endsWith(".sqlite")) {
      return "sqlite";
    }
    if (lower.endsWith(".sql")) {
      return "sql";
    }
    if (lower.endsWith(".db")) {
      return "db";
    }
    return null;
  },

  isAllowedStandaloneFile(file) {
    return allowedStandaloneExtensions.includes(
      this.getFileExtension(file.name)
    );
  },

  getUploadContentType(ext, file) {
    if (file?.type) {
      return file.type;
    }
    switch (ext) {
      case "zip":
        return "application/zip";
      case "tar.gz":
      case "sql.gz":
        return "application/gzip";
      case "sql":
        return "application/sql";
      case "sqlite":
      case "db":
        return "application/x-sqlite3";
      default:
        return "application/octet-stream";
    }
  },

  getUploadFilename(name) {
    return name || "";
  },

  flattenSelections(selections) {
    const files = [];
    for (const selection of selections) {
      for (const file of selection.files) {
        files.push(file);
      }
    }
    return files;
  },

  normalizePayloadFiles(payload) {
    if (payload?.uploadItems?.length) {
      return payload.uploadItems;
    }
    if (payload?.files?.length) {
      return payload.files;
    }
    if (payload?.file) {
      return [payload.file];
    }
    return [];
  },
};

export const deployFromZipUploadMixin = {
  data() {
    return {
      maxFileSelections: 5,
    };
  },
  methods: deployFromZipUploadMethods,
};

export default {
  name: "OnboardingDeployFromZipModal",
  components: {StepIndicator},
  mixins: [deployFromZipUploadMixin],
  props: {
    isOpen: {type: Boolean, default: false},
    loading: {type: Boolean, default: false},
  },
  data: () => ({
    selections: [],
    errorMessage: null,
    selectionIdCounter: 0,
    isDragOver: false,
    dragDepth: 0,
    isReadingDrop: false,
    supportedFormatKeys: [
      "webApp",
      "database",
      "backup",
      "allInOne",
      "separateFiles",
    ],
  }),
  computed: {
    isAtSelectionLimit() {
      return this.selections.length >= this.maxFileSelections;
    },
    isContinueDisabled() {
      return this.loading || !this.selections.length;
    },
    isDropZoneDisabled() {
      return this.loading || this.isAtSelectionLimit || this.isReadingDrop;
    },
  },
  methods: {
    resetData() {
      this.selections = [];
      this.errorMessage = null;
      this.isDragOver = false;
      this.dragDepth = 0;
      this.isReadingDrop = false;
      this.clearNativeInputs();
    },
    clearNativeInputs() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      if (this.$refs.folderInput) {
        this.$refs.folderInput.value = "";
      }
    },
    onDropZoneClick() {
      if (this.isDropZoneDisabled) {
        return;
      }
      this.triggerFileInput();
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    triggerFolderInput() {
      if (this.isDropZoneDisabled) {
        return;
      }
      this.$refs.folderInput.click();
    },
    onDragEnter() {
      if (this.isDropZoneDisabled) {
        return;
      }
      this.dragDepth += 1;
      this.isDragOver = true;
    },
    onDragOver() {
      if (this.isDropZoneDisabled) {
        return;
      }
      this.isDragOver = true;
    },
    onDragLeave() {
      this.dragDepth -= 1;
      if (this.dragDepth <= 0) {
        this.dragDepth = 0;
        this.isDragOver = false;
      }
    },
    async onDrop(event) {
      this.isDragOver = false;
      this.dragDepth = 0;

      if (this.isDropZoneDisabled) {
        return;
      }

      const dataTransfer = event.dataTransfer;
      if (!dataTransfer) {
        return;
      }

      this.errorMessage = null;
      this.isReadingDrop = true;

      try {
        const items = dataTransfer.items;
        const entries = [];

        if (items?.length) {
          for (let i = 0; i < items.length; i += 1) {
            const entry = items[i].webkitGetAsEntry?.();
            if (entry) {
              entries.push(entry);
            }
          }
        }

        if (entries.length) {
          await this.processDroppedEntries(entries);
        } else {
          await this.processDroppedFileList(
            Array.from(dataTransfer.files || [])
          );
        }
      } finally {
        this.isReadingDrop = false;
      }
    },
    readAllDirectoryEntries(reader) {
      return new Promise((resolve) => {
        const entries = [];

        const readBatch = () => {
          reader.readEntries((batch) => {
            if (!batch.length) {
              resolve(entries);
              return;
            }
            entries.push(...batch);
            readBatch();
          });
        };

        readBatch();
      });
    },
    getFileFromEntry(entry) {
      return new Promise((resolve, reject) => {
        entry.file(resolve, reject);
      });
    },
    fileWithRelativePath(file, relativePath) {
      try {
        Object.defineProperty(file, "webkitRelativePath", {
          value: relativePath,
          configurable: true,
        });
      } catch (e) {
        // Some environments may not allow redefining the property.
      }
      return file;
    },
    async collectFilesFromEntry(entry, pathPrefix) {
      if (entry.isFile) {
        const file = await this.getFileFromEntry(entry);
        return [this.fileWithRelativePath(file, pathPrefix + file.name)];
      }

      if (entry.isDirectory) {
        const reader = entry.createReader();
        const childEntries = await this.readAllDirectoryEntries(reader);
        const nextPrefix = pathPrefix + entry.name + "/";
        let files = [];

        for (const child of childEntries) {
          files = files.concat(
            await this.collectFilesFromEntry(child, nextPrefix)
          );
        }

        return files;
      }

      return [];
    },
    async processDroppedEntries(entries) {
      if (!this.canAddSelections(entries.length)) {
        return;
      }

      const directoryEntries = entries.filter((entry) => entry.isDirectory);
      const fileEntries = entries.filter((entry) => entry.isFile);
      const standaloneFiles = [];

      for (const entry of fileEntries) {
        standaloneFiles.push(await this.getFileFromEntry(entry));
      }

      if (
        standaloneFiles.length &&
        !this.validateStandaloneFiles(standaloneFiles)
      ) {
        return;
      }

      for (const entry of directoryEntries) {
        const files = await this.collectFilesFromEntry(entry, "");

        if (!files.length) {
          this.errorMessage = this.$t(
            "heading.onboarding.zipDeploy.file.emptyFolder"
          );
          continue;
        }

        this.appendDirectorySelection(files);
      }

      for (const file of standaloneFiles) {
        this.appendFileSelection(file);
      }
    },
    async processDroppedFileList(files) {
      if (!files.length) {
        return;
      }

      const hasRelativePaths = files.some((file) => file.webkitRelativePath);

      if (hasRelativePaths) {
        if (!this.canAddSelections(1)) {
          return;
        }
        this.appendDirectorySelection(files);
        return;
      }

      this.addFileSelections(files);
    },
    appendFileSelection(file) {
      this.selections = [
        ...this.selections,
        {
          id: this.nextSelectionId(),
          kind: "file",
          label: file.name,
          files: [file],
        },
      ];
    },
    appendDirectorySelection(files) {
      this.selections = [
        ...this.selections,
        {
          id: this.nextSelectionId(),
          kind: "directory",
          label: this.getFolderSelectionLabel(files),
          files,
        },
      ];
    },
    addFileSelections(files) {
      if (!files.length) {
        return;
      }

      this.errorMessage = null;

      if (!this.validateStandaloneFiles(files)) {
        return;
      }

      if (!this.canAddSelections(files.length)) {
        return;
      }

      const newSelections = files.map((file) => ({
        id: this.nextSelectionId(),
        kind: "file",
        label: file.name,
        files: [file],
      }));

      this.selections = [...this.selections, ...newSelections];
    },
    nextSelectionId() {
      this.selectionIdCounter += 1;
      return `selection-${this.selectionIdCounter}`;
    },
    getFolderLabel(files) {
      const firstPath = files[0]?.webkitRelativePath || files[0]?.name || "";
      const root = firstPath.split("/")[0];
      return root || firstPath || this.$t("heading.onboarding.zipDeploy.file.addFolder");
    },
    getFolderSelectionLabel(files) {
      return this.$t("heading.onboarding.zipDeploy.selection.folderSummary", {
        name: this.getFolderLabel(files),
        count: files.length,
      });
    },
    removeSelection(id) {
      this.selections = this.selections.filter((item) => item.id !== id);
      this.errorMessage = null;
    },
    validateStandaloneFiles(files) {
      const invalid = files.filter((file) => !this.isAllowedStandaloneFile(file));
      if (invalid.length) {
        this.errorMessage = this.$t("heading.onboarding.zipDeploy.file.invalidType");
        return false;
      }
      return true;
    },
    canAddSelections(count) {
      if (this.selections.length + count > this.maxFileSelections) {
        this.errorMessage = this.$t("heading.onboarding.zipDeploy.file.maxSelections");
        return false;
      }
      return true;
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      this.clearNativeInputs();
      this.addFileSelections(files);
    },
    onFolderSelected(event) {
      const files = Array.from(event.target.files || []);
      this.clearNativeInputs();

      if (!files.length) {
        this.errorMessage = this.$t("heading.onboarding.zipDeploy.file.emptyFolder");
        return;
      }

      this.errorMessage = null;

      if (!this.canAddSelections(1)) {
        return;
      }

      this.appendDirectorySelection(files);
    },
    submit() {
      if (this.isContinueDisabled) {
        return;
      }

      this.errorMessage = null;
      const uploadItems = this.flattenSelections(this.selections);

      if (!uploadItems.length) {
        this.errorMessage = this.$t("heading.onboarding.zipDeploy.file.empty");
        return;
      }

      this.$emit("next", {uploadItems});
    },
  },
  watch: {
    isOpen: function (value) {
      this.resetData();
      if (value) {
        this.$nextTick(() => {
          this.$store.dispatch("lockBodyScroll");
        });
      } else {
        this.$store.dispatch("unlockBodyScroll");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.v-card::v-deep {
  .v-card__text {
    -webkit-line-clamp: unset !important;
  }

  .v-text-field--outlined > .v-input__control > .v-input__slot {
    background-color: #fff;
  }

  .form-box {
    border: 1px solid var(--v-gray-lighten4);
    border-radius: 8px;
    padding: 16px;
  }

  .drop-zone {
    border: 2px dashed var(--v-gray-lighten2);
    border-radius: 8px;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    background-color: #fff;

    &--active {
      border-color: var(--v-primary-base);
      background-color: var(--v-primary-lighten5);
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &--reading {
      cursor: wait;
    }

    &__title {
      text-align: center;
    }
  }

  .select-folder-link {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .selection-list {
    max-height: 160px;
    overflow-y: auto;

    &__item {
      padding: 4px 0;
      border-bottom: 1px solid var(--v-gray-lighten4);

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .supported-formats__list {
    li {
      margin-bottom: 2px;
    }
  }

  .error-box {
    background-color: var(--v-error-lighten5);
    border-radius: 8px;
    padding: 16px;
    display: flex;
  }

  @media (max-width: 480px) {
    .v-card__text,
    .v-card__actions {
      padding-left: 24px !important;
      padding-right: 24px !important;
    }

  }
}
</style>
